import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { CENTER_TABLE_BASE } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-center-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './center-master.component.html',
  styleUrl: './center-master.component.scss',
})
export class CenterMasterComponent implements OnInit {
  view: 'list' | 'form' = 'list';

  records: any[] = [];
  managers: any[] = [];
  isListLoading = false;

  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  tableConfig: VT2_TABLE_CONFIG = {
    ...CENTER_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Center Master', 'Manage centers and manager assignments');
    this.buildForm();
    this.loadManagers();
    this.loadList();
  }

  buildForm(): void {
    this.form = this.fb.group({
      centerId:   ['', [Validators.required, Validators.maxLength(20)]],
      centerName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      address:    ['', [Validators.maxLength(500)]],
      area:       ['', [Validators.maxLength(100)]],
      pincode:    ['', [Validators.maxLength(10)]],
      managerId:  ['', [Validators.required]],
      isActive:   [true],
    });
  }

  loadManagers(): void {
    this.api.getManagers().subscribe({
      next: (data) => { this.managers = data || []; },
      error: () => { this.managers = []; },
    });
  }

  getManagerName(managerId: string): string {
    const m = this.managers.find(r => r._id === managerId);
    return m ? m.name : managerId;
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getCenters().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset({ isActive: true });
    this.successMessage = '';
    this.errorMessage = '';
    this.view = 'form';
    this.pageTitle.set('Center Master', 'Add a new center');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id;
    this.successMessage = '';
    this.errorMessage = '';
    this.form.patchValue({
      centerId:   record.centerId,
      centerName: record.centerName,
      address:    record.address,
      area:       record.area,
      pincode:    record.pincode,
      managerId:  record.managerId,
      isActive:   record.isActive,
    });
    this.view = 'form';
    this.pageTitle.set('Center Master', 'Update center details');
  }

  backToList(): void {
    this.view = 'list';
    this.pageTitle.set('Center Master', 'Manage centers and manager assignments');
    this.loadList();
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteCenter(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete center.';
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const request$ = this.isEditMode
      ? this.api.updateCenter({ ...this.form.value, id: this.editId })
      : this.api.saveCenter(this.form.value);

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Center updated successfully!' : 'Center saved successfully!';
        setTimeout(() => this.backToList(), 1000);
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.message || 'Failed to save center.';
      },
    });
  }
}
